import moment from 'moment';
import {
  CreditCard,
  InvoiceGroup,
  PaymentMethodType,
  PaymentOnTerms,
  BillingFrequency,
  EntitlementDetails,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getCustomPromotionText, getPromotionText, OfferingOption, UserTierOption } from '@css-xp/base';
import { SelectedPromotion } from '@css-xp/base/forms';
import { getOrderPreviewMessage } from './review-change-helper';
import { FormState, OrderPreviewData, Estimate } from '../manage-entitlement-v2';
import { formatToUSD } from '../utils/currency-display';
import { isFreePlanEntitlement, isFreePricingType } from '@atlassian/cssxp-utils-entitlement';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { getAppliedPromotions } from '../manage-entitlement-helper';
import { BillEstimateOrderItemDto } from '@atlassian/cssxp-ccp-types';

type EntitlementType = {
  entitlementId: EntitlementDetails['id'] | LinkedDestinationEntitlement['entitlementId'];
  entitlementNumber: EntitlementDetails['entitlementNumber'];
  productName: EntitlementDetails['productName'];
  productId?: EntitlementDetails['productId'];
  billingFrequency: BillingFrequency;
  offering?: OfferingOption['label'];
  userTier?: UserTierOption['label'] | null;
  units?: string | null;
  appliedPromotions: SelectedPromotion[];
};

type Props = {
  entitlement: EntitlementDetails;
  formState: FormState;
  isBillingCycleChanged: boolean;
  estimatedBill: Estimate;
  linkedEntitlements: LinkedDestinationEntitlement[];
  anchorTime: number | null | undefined;
  availableInvoiceGroups: InvoiceGroup[] | never[] | undefined;
  orderPreviewData: OrderPreviewData;
  showDuration: boolean;
  isAnnualTermedSelected: boolean;
  isGLPEstimationEnabled: boolean;
  startDate: number | undefined;
  endTimestampValue: number | null | undefined;
};

const getDiscountsText = (appliedPromotions: SelectedPromotion[]) => {
  const discounts = !appliedPromotions?.length
    ? ' N/A'
    : appliedPromotions
        .map((promotion) => {
          const promotionText = promotion.customFormField
            ? getCustomPromotionText(promotion)
            : getPromotionText(promotion).join('\n');
          return `\n${promotionText}`;
        })
        .join('');

  return `\nDiscount applied (to be reflected in the next invoice):${discounts}`;
};

export const copyReviewChangeContentV2 = ({
  entitlement,
  formState,
  isBillingCycleChanged,
  estimatedBill,
  linkedEntitlements,
  anchorTime,
  availableInvoiceGroups,
  orderPreviewData,
  isAnnualTermedSelected,
  isGLPEstimationEnabled,
  showDuration,
  startDate,
  endTimestampValue,
}: Props) => {
  const isStatuspageProduct = isStatuspage(entitlement?.productId);
  const currentEntitlement = {
    entitlementId: entitlement?.id,
    entitlementNumber: entitlement?.entitlementNumber,
    productName: entitlement?.productName,
    productId: entitlement?.productId,
    billingFrequency: formState?.billingFrequency,
    offering: formState?.offering?.label,
    userTier:
      !isFreePricingType(formState?.offering?.pricingType) && !isStatuspageProduct ? formState?.userTier?.label : null,
    units: isStatuspageProduct ? formState?.units : null,
    appliedPromotions: getAppliedPromotions(formState, isBillingCycleChanged, entitlement),
  };
  const linkedEntitlementsData = linkedEntitlements?.map((linkedEntitlement) => ({
    entitlementId: linkedEntitlement?.entitlementId,
    entitlementNumber: linkedEntitlement?.entitlementNumber,
    productName: linkedEntitlement?.productName,
    billingFrequency: formState?.billingFrequency,
    offering: formState?.[linkedEntitlement?.entitlementId]?.offering?.label,
    userTier: isFreePricingType(formState?.offering?.pricingType)
      ? null
      : formState?.[linkedEntitlement?.entitlementId]?.userTier?.label,
    appliedPromotions: getAppliedPromotions(
      formState[linkedEntitlement?.entitlementId],
      isBillingCycleChanged,
      linkedEntitlement
    ),
  }));
  const invoiceGroup = availableInvoiceGroups?.find((item) => item.id === formState.invoiceGroupId?.value);
  const currentEntitlementBillEstimate = estimatedBill?.response?.estimate;
  const linkedEntitlementsBillEstimate = estimatedBill?.response?.linkedEstimates ?? [];
  const isOnTermPayment = invoiceGroup?.defaultPaymentMethod?.type === PaymentMethodType.DEFERRED;
  let billDateText =
    !isStatuspageProduct && anchorTime ? `\nBill anchor date: ${moment.utc(anchorTime).format('MMM DD')}` : '';
  let paymentMethodSection;
  let copyReviewChangeContentTemplate = '';

  if (isOnTermPayment) {
    const paymentTerm = invoiceGroup?.defaultPaymentMethod as PaymentOnTerms;
    paymentMethodSection = `\nPayment terms\nPay within ${paymentTerm?.terms?.invoiceDueDays === 14 ? 14 : 30} days\n`;
  } else {
    const creditCard = invoiceGroup?.defaultPaymentMethod as CreditCard;
    paymentMethodSection = creditCard?.card?.last4Numbers
      ? `\nPayment method\nCard **** **** **** ${creditCard?.card?.last4Numbers}\n`
      : '';
  }

  if (formState.billingFrequency === BillingFrequency.ANNUAL) {
    const billPeriodText = showDuration ? `\nBilling period: ${formState.termLength?.value ?? 12} months` : '';
    const purchaseText = `\nPurchase: ${isAnnualTermedSelected ? 'Annual termed' : 'Auto-renew'}`;
    const transitionTime = orderPreviewData?.response?.items?.[0]?.processingInfo?.transitionTime;
    const isBillingCycleChangeOrder = isBillingCycleChanged || isFreePlanEntitlement(entitlement);
    const transitionTimeText =
      !orderPreviewData.error && transitionTime ? `\n${getOrderPreviewMessage(transitionTime)}` : '';
    const subscriptionDetails = `\nSubscription details:${billDateText}${billPeriodText}${purchaseText}${transitionTimeText}\n`;
    let totalEstimate = 0;
    const entitlementDetails = [currentEntitlement, ...linkedEntitlementsData]
      .map((item: EntitlementType) => {
        const itemEstimate = [currentEntitlementBillEstimate, ...linkedEntitlementsBillEstimate]?.find(
          (billItem) => billItem?.entitlementId === item.entitlementId
        );

        const entitlementEstimate =
          !isStatuspageProduct && (!isBillingCycleChangeOrder || isGLPEstimationEnabled)
            ? (itemEstimate?.saleTransitionDetails?.glpForNextPlan ?? 0) / 100
            : itemEstimate?.grandTotal ?? 0;
        totalEstimate += entitlementEstimate;
        const priceText = `\n${
          isAnnualTermedSelected ? 'Annual price estimate' : 'Recurring annual price'
        }: ${formatToUSD(entitlementEstimate)}`;
        const planText = `\nPlan: ${item.offering}`;
        const userTierText = item.userTier ? `\nUser tier: ${item.userTier}` : '';
        const discountText = getDiscountsText(item.appliedPromotions);
        const proRatedEstimate =
          !isBillingCycleChangeOrder && !isStatuspageProduct
            ? `\nPro-rated estimate: ${formatToUSD(itemEstimate?.grandTotal ?? 0)}`
            : '';

        return `\nSubscription: ${item.productName} ${item.entitlementNumber}${priceText}${planText}${userTierText}${discountText}${proRatedEstimate}\n`;
      })
      .join('');

    const footerConsentLinks = `\n\nBy consenting, you also confirm you agree to the Atlassian Cloud Terms of Service and acknowledge the Privacy Policy. You can find these at:\nAtlassian Cloud Terms of Service: https://www.atlassian.com/legal/cloud-terms-of-service\nPrivacy Policy: https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers\n\nChanged your mind?\nIf you don’t want to be charged, don’t worry – you don’t need to do anything. If you want to cancel your subscription at any point, you can reach out to your Customer Advocate.\n\nThanks,\nAtlassian Support`;
    const scheduledChangesText = `${
      !isAnnualTermedSelected
        ? '\n\nFor scheduled changes, the changes will take effect from the end of the current billing cycle (or the trial end date). Please note that scheduled changes are only applicable to annual auto-renew subscriptions.\n'
        : '\n'
    }`;

    if (isBillingCycleChangeOrder) {
      const greetingsSection = `Hi,\nWe’ve received your request to change the billing cycle for your subscription to annual ${
        isAnnualTermedSelected ? 'termed' : 'auto-renew'
      }. In order for us to charge you annually, you’ll need to review the details below and give your consent by responding on the GSAC ticket with “Yes” or “I agree”.\n\nOnce we receive your consent, your order will be placed and you’ll receive your invoice. This means that the subscription start date will depend on when we receive your consent and when the order is placed. We’ll advise you each year when your payment’s due, and charge your chosen payment method at that time.${scheduledChangesText}`;

      const entitlementsSection = `\nAffected product family:${entitlementDetails}`;
      const totalEstimateSection = `\nTotal estimate: ${formatToUSD(totalEstimate)}\n`;

      const footerSection = `\nBefore you continue:${
        !isAnnualTermedSelected
          ? `\nBy responding with your consent, you acknowledge that your order contains annual auto-renewal subscription(s). You'll automatically be billed ${formatToUSD(
              totalEstimate
            )} per year ${
              !isStatuspageProduct ? `(plus taxes and any applicable discounts, promotions, or credits)` : ''
            } until you cancel your subscription(s) at any time. Our prices may change upon notice to you.`
          : ''
      }\nTo cancel, go to Subscriptions page, select Manage > Cancel subscription, and follow the instructions.${
        !isStatuspageProduct
          ? `\n\nKeep in mind that the actual amount charged may be higher or lower than the estimate, as the estimate excludes taxes and other fees, and any pro-rated amounts. The charged amount will also reflect any applicable discounts, promotions or credits. You’ll receive an invoice with the final amount. In some cases, you may first be charged a pro-rated amount to align your bill date with your other subscriptions.`
          : ''
      }${footerConsentLinks}`;
      copyReviewChangeContentTemplate = `${greetingsSection}${subscriptionDetails}${entitlementsSection}${totalEstimateSection}${paymentMethodSection}${footerSection}`;
    } else {
      const greetingsSection = `Hi,\nWe’ve received your request to update your annual subscription. In order for us to charge you based on the updates, you’ll need to review the details below and give your consent by responding on the GSAC ticket with “Yes” or “I agree”.\n\nOnce we receive your consent, your order will be placed and you’ll receive your invoice. This means that the changes will come into affect depending on when we receive your consent and when the order is placed. We’ll advise you when your payment’s due, and charge your chosen payment method at that time.\n\nFor immediate changes, the first invoice will be pro-rated until the next bill date. You’ll then be charged the full amount of your next invoice on a recurring annual basis.${
        !isStatuspageProduct
          ? ' Please note that a recurring invoice is only applicable for auto-renew plans – termed subscriptions will end after the term is completed.'
          : ''
      }${scheduledChangesText}`;

      if (isAnnualTermedSelected) {
        billDateText = `\nStart date:  ${moment.utc(startDate).format('MMM DD, YYYY')} \nEnd date: ${moment
          .utc(endTimestampValue)
          .format('MMM DD, YYYY')}`;
      }
      const subscriptionDetails = `\nSubscription details:${billDateText}${billPeriodText}${purchaseText}${transitionTimeText}\n`;

      const totalEstimateSection = isAnnualTermedSelected
        ? `\nTotal amount: ${formatToUSD(totalEstimate)}\n`
        : `\nTotal recurring amount: ${formatToUSD(totalEstimate)}\n`;

      const footerSection = `\nBefore you continue:\nBy responding with your consent, you acknowledge that your order contains annual ${
        isAnnualTermedSelected ? 'termed' : 'auto-renewal'
      } subscription(s). You'll automatically be billed ${formatToUSD(
        totalEstimate
      )} per year until you cancel your subscription(s) at any time${
        !isStatuspageProduct
          ? ' (Please note annual termed subscriptions get auto cancelled at the end of their term)'
          : ''
      }. Our prices may change upon notice to you. To cancel, go to Subscriptions, select Manage > Cancel subscription, and follow the instructions.${footerConsentLinks}`;

      const entitlementsSection = `\nAffected product family:${entitlementDetails}`;
      copyReviewChangeContentTemplate = `${greetingsSection}${subscriptionDetails}${entitlementsSection}${totalEstimateSection}${paymentMethodSection}${footerSection}`;
    }
  }

  navigator.clipboard.writeText(copyReviewChangeContentTemplate);
};
